import {assert} from 'Asserts';
import * as ast from 'notebook/tables/excess/AST';
import {RelativeCellAddress} from 'notebook/tables/excess/Evaluator';
import {Token} from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';
import {Table, TABLE_COLUMN, TABLE_ROW, TableDimension} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {assertNever} from 'util/Types';

/**
 * A function that transforms a token.
 *
 * @param token             The token to update
 * @param cellPosition      The position of the cell containing the token
 * @returns                 The new text for the token, or null if the token should not be updated.
 */
export type TokenUpdater = (token: Token, cellPosition: RelativeCellAddress) => string | null;

function isReferenceToOtherDocument(
  documentName: string | null,
  node: ast.Cell | ast.CellRange,
  cellPosition: RelativeCellAddress
): boolean {
  const cellDocument = node.getDoc() ?? cellPosition.document;
  return documentName != null && cellDocument != null && cellDocument !== documentName;
}

/**
 * @param dim              Whether we are deleting rows or columns
 * @param minK             The smaller index dimension
 * @param maxK             The larger index dimension
 * @return                 Token updater function for deleting elements [minK, maxK] in dimension `dim`
 *                         from the given table
 */
export function createDeleteTokenUpdater(
  table: Table,
  documentName: string | null,
  dim: TableDimension,
  minK: number,
  maxK: number
): TokenUpdater {
  const targetTableName = table.name;
  assert([TABLE_ROW, TABLE_COLUMN].includes(dim), `Unexpected dimension ${dim}`);
  const dimShift = maxK - minK + 1;
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }

    const {node} = token;
    if (isReferenceToOtherDocument(documentName, node, cellPosition)) {
      return null;
    }
    // Skip tokens that aren't cell references for the target table.
    if ((node.getTable() ?? cellPosition.table) !== targetTableName) {
      return null;
    }

    if (node instanceof ast.Cell) {
      if (dim === TableDimension.ROW) {
        // If a referencing a Single Input, don't shift.
        if (isSingleInputReference(table, node)) {
          return null;
        }
      }
      // Handle shifting reference for Cell
      if (node.id[dim] < minK) {
        return null;
      }
      if (node.id[dim] <= maxK) {
        return util.ReferenceError.TEXT;
      }

      const newDim = node.id[dim] - dimShift;
      const newId = {
        table: node.getTable(),
        document: node.getDoc(),
        row: node.id.row,
        column: node.id.column,
      };
      newId[dim] = newDim;
      return util.toCellReferenceTokenText(newId, node.locks);
    } else if (node instanceof ast.CellRange) {
      // Handle shifting references for CellRanges
      const {start, end} = node;

      // Sort to simplify processing
      const isSmallerFirst = start[dim] <= end[dim];
      const [smallerId, largerId] = isSmallerFirst ? [start, end] : [end, start];

      // Determine new min row
      let newSmallerDim: number;
      if (smallerId[dim] < minK) {
        newSmallerDim = smallerId[dim];
      } else if (smallerId[dim] <= maxK) {
        // Given the cell range extends from smallerDim->largerDim, when minK <= smallerDim <= maxK, the
        // desired range to keep was originally (maxK + 1)->largerDim. (maxK + 1) maps to minK after
        // applying the shift by dimShift of (maxK - minK + 1).
        newSmallerDim = minK;
      } else {
        newSmallerDim = smallerId[dim] - dimShift;
      }

      // Determine new max row
      let newLargerDim: number;
      if (largerId[dim] < minK) {
        newLargerDim = largerId[dim];
      } else if (largerId[dim] <= maxK) {
        // Given the cell range extends from smallerDim->largerDim, when minK <= largerDim <= maxK, the
        // desired range to keep extends from smallerDim->(minK - 1).
        newLargerDim = minK - 1;
      } else {
        newLargerDim = largerId[dim] - dimShift;
      }

      // If there was no change, continue.
      if (newSmallerDim === smallerId[dim] && newLargerDim === largerId[dim]) {
        return null;
      }
      if (newSmallerDim > newLargerDim) {
        // range is now invalid (e.g. range only containing deleted row)
        return util.ReferenceError.TEXT;
      }

      const newSmallerId = {...smallerId, [dim]: newSmallerDim};
      const newLargerId = {...largerId, [dim]: newLargerDim};

      // Undo the simplifying sort we did earlier
      const [newStartId, newEndId] = isSmallerFirst
        ? [newSmallerId, newLargerId]
        : [newLargerId, newSmallerId];

      return util.toCellRangeReferenceTokenText(newStartId, newEndId, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * @param targetTableName  The table in which cells were inserted
 * @param dim              Whether we are inserting rows or columns
 * @param insertIndex      The index of the row before which the rows / columns were inserted.
 * @param numNew         The number of rows / columns to insert
 * @return                 TokenUpdater function for inserting `dimShift` rows / columns (along the
 *                         specified `dim`) at the `k`th row / column on the given table.
 */
export function createInsertTokenUpdater(
  table: Table,
  documentName: string | null,
  dim: TableDimension,
  insertIndex: number,
  numNew: number
): TokenUpdater {
  assert(numNew > 0, 'Expected positive numNew', {numNew});
  const targetTableName = table.name;
  const dimShift = numNew; // Rename parameter to match the delete token updater.
  assert([TABLE_ROW, TABLE_COLUMN].includes(dim), `Unexpected dimension ${dim}`);
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }

    const {node} = token;
    // If doc is set, skip tokens that aren't cell references for the target doc
    if (isReferenceToOtherDocument(documentName, node, cellPosition)) {
      return null;
    }
    // Skip tokens that aren't cell references for the target table.
    if ((node.getTable() ?? cellPosition.table) !== targetTableName) {
      return null;
    }

    // Handle shifting reference for Cell
    if (node instanceof ast.Cell) {
      if (dim === TableDimension.ROW) {
        // If referencing a Single Input, don't shift.
        if (isSingleInputReference(table, node)) {
          return null;
        }
      }
      // If cell is before the insertion point, it will not move
      if (node.id[dim] < insertIndex) {
        return null;
      }

      // Shift reference by `dimShift` since it moves down `dimShift` rows / columns
      const newDim = node.id[dim] + dimShift;
      const newId = {...node.id, [dim]: newDim};
      return util.toCellReferenceTokenText(newId, node.locks);
    } else if (node instanceof ast.CellRange) {
      // Handle shifting references for CellRanges
      const {start, end} = node;

      // Sort to simplify processing
      const isSmallerFirst = start[dim] <= end[dim];
      const [smallerId, largerId] = isSmallerFirst ? [start, end] : [end, start];

      // Shift the endpoints by `dimShift` if the endpoint is at or after the insertion point `k`
      const newSmallerDim = smallerId[dim] + (smallerId[dim] < insertIndex ? 0 : dimShift);
      const newLargerDim = largerId[dim] + (largerId[dim] < insertIndex ? 0 : dimShift);

      // If there was no change, continue.
      if (newSmallerDim === smallerId[dim] && newLargerDim === largerId[dim]) {
        return null;
      }

      const newSmallerId = {...smallerId, [dim]: newSmallerDim};
      const newLargerId = {...largerId, [dim]: newLargerDim};

      // Undo the simplifying sort we did earlier
      const [newStartId, newEndId] = isSmallerFirst
        ? [newSmallerId, newLargerId]
        : [newLargerId, newSmallerId];

      return util.toCellRangeReferenceTokenText(newStartId, newEndId, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * This token updater should be used when the rows are sorted.
 *
 * @param table            The table from which cells were moved
 * @param documentName     The name of the document from which cells were moved
 * @param targetTableName  The table from which cells were moved
 * @param dim              Dimension that was reordered. Only supports TABLE_ROW
 * @param newToOldIndices  Array mapping from {i -> j} where i is the new row position and j is the old position
 * @return                 Token updater function for applying the given reordering to table
 */
export function createSortRowsTokenUpdater(
  table: Table,
  documentName: string | null,
  dim: TableDimension,
  newToOldIndices: Array<number>
): TokenUpdater {
  assert([TABLE_ROW].includes(dim), `Unexpected dimention ${dim}. Only supports reordering rows`);

  return (token, cellPosition) => {
    const newCellPos = {row: cellPosition.row, column: cellPosition.column};
    const oldCellPos = {...newCellPos, [dim]: newToOldIndices[newCellPos[dim]]};
    if (
      // Sorting only updates formulas in the table that was modified
      cellPosition.table !== table.name ||
      // Sort only updates formulas in moved cells
      newCellPos[dim] === oldCellPos[dim]
    ) {
      return null;
    }

    // Skip tokens that aren't cell references for the target table.
    if (!token.isCellRef()) {
      return null;
    }
    const {node} = token;
    if (isReferenceToOtherDocument(documentName, node, cellPosition)) {
      return null;
    }
    if (
      // Skip tokens that explicitly reference a table (either their own or another table)
      // This is explicitly different from the other token updaters, which update references
      // to the same table. This is to match behavior in Excel and Sheets.
      node.getTable() != null
    ) {
      return null;
    }

    if (node instanceof ast.Cell) {
      // Skip updating references that are locked
      if (node.locks[dim === TableDimension.ROW ? 'row' : 'col']) {
        return null;
      }

      const newNode = node.toRelativeNode(oldCellPos).toAbsoluteNode(newCellPos);

      if (newNode instanceof ast.ReferenceErrorNode) {
        return util.ReferenceError.TEXT;
      }

      // Skip updating tokens that have not changed
      if (node.id[dim] === newNode.id[dim]) {
        return null;
      }

      return util.toCellReferenceTokenText(newNode.id, node.locks);
    } else if (node instanceof ast.CellRange) {
      const isStartLocked = node.locks[dim === TableDimension.ROW ? 'startRow' : 'startCol'];
      const isEndLocked = node.locks[dim === TableDimension.ROW ? 'endRow' : 'endCol'];

      // Skip updating ranges that are entirely locked
      if (isStartLocked && isEndLocked) {
        return null;
      }

      const newNode = node.toRelativeNode(oldCellPos).toAbsoluteNode(newCellPos);

      if (newNode instanceof ast.ReferenceErrorNode) {
        return util.ReferenceError.TEXT;
      }

      // Only change the range endpoints that are not locked
      const newStart = (isStartLocked ? node : newNode).start;
      const newEnd = (isEndLocked ? node : newNode).end;

      // Skip updating tokens that have not changed
      if (newStart[dim] === node.start[dim] && newEnd[dim] === node.end[dim]) {
        return null;
      }

      return util.toCellRangeReferenceTokenText(newStart, newEnd, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * This token updater should be used when the rows or columns are reordered manually.
 *
 * @param table                The table from which cells were moved
 * @param documentName         The name of the document from which cells were moved
 * @param selectionStartIndex  First index of elements selected to move, inclusive, in original indexing order
 * @param selectionEndIndex    Last index of elements selected to move, inclusive, in original indexing order
 * @param newToOldIndices      Array mapping from {i -> j} where i is the new row/column position and j is the old position
 * @param newToOldIndices      Array mapping from {i -> j} where i is the new row position and j is the old position
 * @return                     Token updater function for applying the given reordering to table
 */
export function createReorderTokenUpdater(
  table: Table,
  documentName: string | null,
  dim: TableDimension,
  selectionStartIndex: number,
  selectionEndIndex: number,
  newToOldIndices: Array<number>
): TokenUpdater {
  // Create reverse map for old index -> new index
  const _oldToNewIndices: Array<number> = [];
  for (let newIndex = 0; newIndex < newToOldIndices.length; newIndex++) {
    _oldToNewIndices[newToOldIndices[newIndex]] = newIndex;
  }
  function getNewIndex(oldIndex: number): number {
    // When the cell reference is outside the table boundaries, _oldToNewIndices[oldIndex] is undefined,
    // so we fall back to oldIndex. This is correct because reordering will never change the position
    // of cells outside the table boundaries.
    return _oldToNewIndices[oldIndex] ?? oldIndex;
  }

  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }
    const {node} = token;
    // If doc is set, skip tokens that aren't cell references for the target doc
    if (isReferenceToOtherDocument(documentName, node, cellPosition)) {
      return null;
    }
    // Skip tokens that aren't cell references for the target table
    if ((node.getTable() ?? cellPosition.table) !== table.name) {
      return null;
    }

    if (node instanceof ast.Cell) {
      const newIndex = getNewIndex(node.id[dim]);
      // Skip updating tokens that have not changed
      if (node.id[dim] === newIndex) {
        return null;
      }

      const newNodeId = {...node.id, [dim]: newIndex};
      return util.toCellReferenceTokenText(newNodeId, node.locks);
    } else if (node instanceof ast.CellRange) {
      let {start: rangeStart, end: rangeEnd} = node;
      if (rangeStart[dim] > rangeEnd[dim]) {
        [rangeStart, rangeEnd] = [rangeEnd, rangeStart];
      }

      // Skip updating ranges that have not changed
      if (getNewIndex(rangeStart[dim]) === rangeStart[dim] && getNewIndex(rangeEnd[dim]) === rangeEnd[dim]) {
        return null;
      }

      const movedRangeStart = {...rangeStart, [dim]: getNewIndex(rangeStart[dim])};
      const movedRangeEnd = {...rangeEnd, [dim]: getNewIndex(rangeEnd[dim])};

      let newRangeStart: RelativeCellAddress;
      let newRangeEnd: RelativeCellAddress;

      const startBelowEnd = movedRangeStart[dim] > movedRangeEnd[dim];
      const startMovedUp = movedRangeStart[dim] < rangeStart[dim];
      const endMovedDown = movedRangeEnd[dim] > rangeEnd[dim];
      const selectionIsSubsetOfRange =
        selectionStartIndex >= rangeStart[dim] && selectionEndIndex <= rangeEnd[dim];
      if (!startBelowEnd && (startMovedUp || endMovedDown || !selectionIsSubsetOfRange)) {
        // Update the range to be the new positions of the original start and end cells
        newRangeStart = movedRangeStart;
        newRangeEnd = movedRangeEnd;
      } else {
        // Update the range to be the subsection of the range that was NOT included in the selection
        const oldStartDim =
          selectionStartIndex <= rangeStart[dim] && selectionEndIndex >= rangeStart[dim] // Does selection include start?
            ? selectionEndIndex + 1 // Then, new start is the first element after selection
            : rangeStart[dim]; // Otherwise, keep the same start
        const oldEndDim =
          selectionStartIndex <= rangeEnd[dim] && selectionEndIndex >= rangeEnd[dim] // Does selection include end?
            ? selectionStartIndex - 1 // Then, new end is the first element before selection
            : rangeEnd[dim]; // Otherwise, keep the same end
        newRangeStart = {...movedRangeStart, [dim]: getNewIndex(oldStartDim)};
        newRangeEnd = {...movedRangeEnd, [dim]: getNewIndex(oldEndDim)};
      }

      // Skip updating tokens that have not changed
      if (newRangeStart[dim] === rangeStart[dim] && newRangeEnd[dim] === rangeEnd[dim]) {
        return null;
      }

      return util.toCellRangeReferenceTokenText(newRangeStart, newRangeEnd, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * @param documentName     The name of the document containing the table being renamed, or null if unknown
 * @param targetTableName  The old name of the table being renamed.
 * @param newTableName     The new name of the table being renamed.
 * @return                 Token updater function for renaming table `targetTableName` to `newTableName`.
 */
export function createRenameTableTokenUpdater(
  documentName: string | null,
  targetTableName: string,
  newTableName: string
): TokenUpdater {
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }

    const {node} = token;
    if (isReferenceToOtherDocument(documentName, node, cellPosition)) {
      return null;
    }
    // Skip tokens that aren't cell references to the target table.
    if (node.getTable() !== targetTableName) {
      return null;
    }

    if (node instanceof ast.Cell) {
      // Handle replacing reference for Cell
      return util.toCellReferenceTokenText(
        {
          ...node.id,
          table: newTableName,
        },
        node.locks
      );
    } else if (node instanceof ast.CellRange) {
      // Handle replacing references for CellRanges
      return util.toCellRangeReferenceTokenText(
        {...node.start, table: newTableName},
        {...node.end, table: newTableName},
        node.locks
      );
    }
    assertNever(node);
    return null;
  };
}

/**
 * @param previousDocumentName The previous name of the document.
 * @param newDocumentName
 * @returns The token updater function for renaming the document.
 */
export function createRenameDocumentTokenUpdater(
  previousDocumentName: string,
  newDocumentName: string
): TokenUpdater {
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }
    const {node} = token;
    if (isReferenceToOtherDocument(previousDocumentName, node, cellPosition)) {
      return null;
    }
    // Do not rename tokens that did not have a reference to the previous document.
    if (node.getDoc() !== previousDocumentName) {
      return null;
    }

    if (node instanceof ast.Cell) {
      return util.toCellReferenceTokenText({...node.id, document: newDocumentName}, node.locks);
    } else if (node instanceof ast.CellRange) {
      return util.toCellRangeReferenceTokenText(
        {...node.start, document: newDocumentName},
        {...node.end, document: newDocumentName},
        node.locks
      );
    }
    assertNever(node);
    return null;
  };
}

/**
 * Returns true if the referenced cell is a Single Input cell.
 *
 * Keep in sync with benchling/notebook/tables/excess/table_transform_helpers.py#is_single_input_reference
 */
function isSingleInputReference(table: Table, node: ast.Cell): boolean {
  return table && node.id.row === 0 && node.id.column < TableUtil.findSingleInputBoundaryIndex(table);
}
